/**
 * Web & app services page bundle.
 * Enqueued only on the page-webdesign-app-entwicklung.php template
 * (inc/enqueue.php). Two jobs:
 *   1. Reveal the hero like the homepage does (that bundle only runs
 *      on the front page), incl. reduced-motion safe fallbacks.
 *   2. Run the 3-step quote configurator: validation, live estimate,
 *      summary, AJAX submit to aad_handle_quote_submit in
 *      inc/webapp-quote.php.
 *
 * The estimate is deliberately data-driven: change the numbers here and
 * the picker cards in the template stay in sync visually.
 */
import { gsap } from 'gsap';
import { prefersReducedMotion } from './utils/motion.js';

/* ---------------------------------------------------------------- *
 * Configuration (keep in sync with the template's labels/prices)
 * ---------------------------------------------------------------- */
const PROJECTS = {
  landing:  { label: 'Landingpage',    base: 890 },
  website:  { label: 'Website',        base: 2400 },
  shop:     { label: 'Onlineshop',     base: 3200 },
  app:      { label: 'Mobile App',     base: 6900 },
  software: { label: 'Softwaresystem', base: 9500 },
};

const SCOPE = {
  compact:  { label: 'Klein · bis 3 Seiten', multi: 1 },
  standard: { label: 'Standard · bis 10',    multi: 1.35 },
  full:     { label: 'Groß · 10+ / Komplex', multi: 1.7 },
};

const LANG = {
  de:     { label: 'Deutsch',               multi: 1 },
  multi2: { label: 'Deutsch + Englisch',    multi: 1.15 },
  multi3: { label: 'Deutsch + 2 weitere',   multi: 1.25 },
};

const EXTRAS = {
  logo:    { label: 'Logo / CI-Entwurf',      multi: 1,  add: 350 },
  seo:     { label: 'SEO-Grundpaket',          multi: 1.1, add: 0 },
  backend: { label: 'Backend / API-Anbindung', multi: 1.25, add: 0 },
  express: { label: 'Express-Termin',          multi: 1.15, add: 0 },
};

/* ---------------------------------------------------------------- *
 * Localized strings & labels. German defaults are baked in so the page
 * works even if Polylang/localization isn't loaded yet; the
 * `aadWebapp` object (wp_localize_script in inc/enqueue.php) overrides
 * them with the current language's values.
 * ---------------------------------------------------------------- */
const STRINGS = {
  pricePrefix: 'ab',
  sending: 'Wird gesendet…',
  send: 'Anfrage senden',
  error: 'Senden fehlgeschlagen — bitte später erneut versuchen.',
  extrasNone: 'Keine',
};

if (window.aadWebapp) {
  const s = window.aadWebapp.strings;
  if (s) {
    if (s.price_prefix) STRINGS.pricePrefix = s.price_prefix;
    if (s.sending) STRINGS.sending = s.sending;
    if (s.send) STRINGS.send = s.send;
    if (s.error) STRINGS.error = s.error;
    if (s.extras_none) STRINGS.extrasNone = s.extras_none;
  }
  const l = window.aadWebapp.labels;
  if (l) {
    if (l.projects) for (const k of Object.keys(PROJECTS)) if (l.projects[k]) PROJECTS[k].label = l.projects[k];
    if (l.scope) for (const k of Object.keys(SCOPE)) if (l.scope[k]) SCOPE[k].label = l.scope[k];
    if (l.lang) for (const k of Object.keys(LANG)) if (l.lang[k]) LANG[k].label = l.lang[k];
    if (l.extras) for (const k of Object.keys(EXTRAS)) if (l.extras[k]) EXTRAS[k].label = l.extras[k];
  }
}

const priceLocale = document.documentElement.lang || 'de-DE';
const priceFormat = new Intl.NumberFormat(priceLocale);
const euro = (n) => `${STRINGS.pricePrefix} ${priceFormat.format(n)}\u00a0€`;

function readState(form) {
  const fd = new FormData(form);
  const extras = [];
  for (const [key, value] of fd.entries()) {
    const m = /^extras\[([a-z]+)\]$/.exec(key);
    if (m && value) extras.push(m[1]);
  }
  return {
    name: fd.get('name') || '',
    company: fd.get('company') || '',
    email: fd.get('email') || '',
    phone: fd.get('phone') || '',
    project: fd.get('project') || 'website',
    scope: fd.get('scope') || 'standard',
    lang: fd.get('lang') || 'de',
    domain: fd.get('domain') || '',
    message: fd.get('message') || '',
    extras,
  };
}

function computeEstimate(state) {
  const project = PROJECTS[state.project];
  let mult = SCOPE[state.scope].multi * LANG[state.lang].multi;
  let add = 0;
  state.extras.forEach((key) => {
    mult *= EXTRAS[key].multi;
    add += EXTRAS[key].add;
  });
  const raw = project.base * mult + add;
  return Math.round(raw / 50) * 50;
}

document.addEventListener('DOMContentLoaded', () => {
  if (!document.body.classList.contains('page-template-page-webdesign-app-entwicklung')) return;

  /* ---------------------------------------------------------------- *
   * 1) Hero reveal (mirrors homepage motion, no pinning)
   * ---------------------------------------------------------------- */
  const heroEls = {
    eyebrow: '.webapp-page .hero .hero__eyebrow',
    title: '.webapp-page .hero .hero__title .line',
    sub: '.webapp-page .hero .hero__sub',
    actions: '.webapp-page .hero .hero__actions',
    cue: '.webapp-page .hero .hero__scroll-cue',
  };

  const show = () => {
    gsap.set(Object.values(heroEls).join(', '), { opacity: 1, y: 0 });
    gsap.set(heroEls.cue, { opacity: 0.6 });
  };

  if (prefersReducedMotion()) {
    show();
  } else {
    gsap.set(heroEls.eyebrow, { opacity: 0, y: 18 });
    gsap.set(heroEls.title, { opacity: 0, y: 26 });
    gsap.set(heroEls.sub, { opacity: 0, y: 18 });
    gsap.set(heroEls.actions, { opacity: 0, y: 18 });
    gsap.set(heroEls.cue, { opacity: 0, y: 10 });
    const tl = gsap.timeline({ delay: 0.1 });
    tl.to(heroEls.eyebrow, { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' })
      .to(heroEls.title, { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', stagger: 0.12 }, '-=0.3')
      .to(heroEls.sub, { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out' }, '-=0.4')
      .to(heroEls.actions, { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' }, '-=0.3')
      .to(heroEls.cue, { opacity: 0.6, y: 0, duration: 0.6 }, '-=0.2');
  }

  /* ---------------------------------------------------------------- *
   * 1b) In-page anchor links (#projekt / #leistungen) scroll smoothly
   * ---------------------------------------------------------------- */
  if (!prefersReducedMotion()) {
    document.querySelectorAll('.webapp-page a[href^="#"]').forEach((link) => {
      link.addEventListener('click', (e) => {
        const target = document.querySelector(link.getAttribute('href'));
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /* ---------------------------------------------------------------- *
   * 2) Multi-step quote form
   * ---------------------------------------------------------------- */
  const form = document.getElementById('quote-form');
  if (!form) return;

  const steps = Array.from(form.querySelectorAll('.quote-form__step'));
  const progress = Array.from(form.querySelectorAll('.quote-form__progress li'));
  const errorEl = document.getElementById('quote-error');
  const submitBtn = form.querySelector('button[type="submit"]');
  let current = 0;

  const goTo = (index) => {
    if (index < 0 || index >= steps.length) return;
    steps[current].classList.remove('is-active');
    current = index;
    steps[current].classList.add('is-active');
    progress.forEach((li, i) => {
      li.classList.toggle('is-active', i === current);
      li.classList.toggle('is-done', i < current);
    });
    steps[current].scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const validateStep = (el) => {
    const fields = el.querySelectorAll('input, textarea');
    for (const f of fields) {
      if (!f.checkValidity()) {
        f.reportValidity();
        return false;
      }
    }
    return true;
  };

  const renderEstimate = (state) => {
    const out = euro(computeEstimate(state));
    document.querySelectorAll('[data-live-estimate], [data-live-estimate-final]').forEach((el) => (el.textContent = out));
    return out;
  };

  const renderSummary = (state) => {
    const map = {};
    map.name = state.name;
    map.company = state.company || '—';
    map.email = state.email;
    map.phone = state.phone;
    map.project = PROJECTS[state.project].label;
    map.scope = SCOPE[state.scope].label;
    map.lang = LANG[state.lang].label;
    map.extras = state.extras.length ? state.extras.map((k) => EXTRAS[k].label).join(', ') : STRINGS.extrasNone;
    map.domain = state.domain || '—';
    map.message = state.message || '—';
    form.querySelectorAll('[data-summary]').forEach((dd) => {
      const key = dd.dataset.summary;
      dd.textContent = map[key] ?? '—';
    });
  };

  // Live estimate while filling in step 2.
  form.addEventListener('input', () => {
    if (current !== 1) return;
    renderEstimate(readState(form));
  });

  // Step navigation.
  form.querySelectorAll('.quote-form__next').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (!validateStep(steps[current])) return;
      if (current === 1) {
        const state = readState(form);
        renderSummary(state);
        renderEstimate(state);
      }
      goTo(current + 1);
    });
  });

  form.querySelectorAll('.quote-form__back').forEach((btn) => {
    btn.addEventListener('click', () => goTo(current - 1));
  });

  // Submit.
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!validateStep(steps[current])) return;

    submitBtn.disabled = true;
    submitBtn.textContent = STRINGS.sending;
    errorEl.hidden = true;

    try {
      const res = await fetch(aadWebapp.ajaxUrl, {
        method: 'POST',
        body: new FormData(form),
      });
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.data?.message || 'Unbekannter Fehler.');
      }
      form.hidden = true;
      const success = document.getElementById('quote-success');
      success?.removeAttribute('hidden');
      if (success) {
        gsap.fromTo(success, { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' });
      }
    } catch (err) {
      errorEl.textContent = err.message || STRINGS.error;
      errorEl.hidden = false;
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = STRINGS.send;
    }
  });
});